import { useMemo } from 'react';
import { selectData } from '@/api/envelope';
import {
  useGetModerationHistory,
  useGetReport,
} from '@/api/generated/moderation/moderation';
import type {
  AdminModerationHistoryResponseDTO,
  AdminReportDetailDTO,
} from '@/api/generated/model';
import type { ModerationDetail } from '../types';
import { toModerationDetail } from '../lib/toModerationDetail';

export interface UseModerationDetailResult {
  report: ModerationDetail | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<unknown>;
}

/**
 * Detail load (doc 09 §4.3): report + history run in parallel; the report resolves to the
 * discriminated union via toModerationDetail once both are (or the report alone is) available. History
 * can lag — the timeline shows the synthetic head only until it fills.
 */
export function useModerationDetail(reportId: string): UseModerationDetailResult {
  const id = Number(reportId);
  const enabled = !!reportId && Number.isFinite(id) && id > 0;

  const reportQ = useGetReport<AdminReportDetailDTO>(id, {
    query: { enabled, select: selectData },
  });
  const historyQ = useGetModerationHistory<AdminModerationHistoryResponseDTO[]>(id, {
    query: { enabled, select: selectData },
  });

  const report = useMemo<ModerationDetail | null>(
    () => (reportQ.data ? toModerationDetail(reportQ.data, historyQ.data ?? []) : null),
    [reportQ.data, historyQ.data],
  );

  return {
    report,
    isLoading: reportQ.isLoading,
    isError: reportQ.isError || historyQ.isError,
    refetch: () => Promise.all([reportQ.refetch(), historyQ.refetch()]),
  };
}
